import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthService } from 'src/app/_services/auth.service';
import { ToastService } from 'src/app/_models/toast.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ModalTransferClaimComponent } from './modal-transferclaim.component';
import { BankAssuranceService } from 'src/app/_services/bank-assurance.service';
import { environment } from 'src/environments/environment';
import { finalize, timeout, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-bancassurance-producteur-transfertsinistre',
  templateUrl: './bancassurance-producteur-transfertsinistre.component.html',
  styleUrls: ['./bancassurance-producteur-transfertsinistre.component.scss']
})
export class BancassuranceProducteurTransfertsinistreComponent implements OnInit {

  activeView: 'toTransfer' | 'history' = 'toTransfer';

  // Données
  claimsToTransfer: any[] = [];
  transferHistory: any[] = [];
  // Pagination / affichage
  pageSize: number = 10;
  currentPage: number = 1;
  totalCount: number = 0;
  totalPages: number = 0;
  displayedClaims: any[] = [];
  displayedHistory: any[] = [];
  loader = false;
  transfering = false;
  transferProgressById: { [id: string]: number } = {};
  selectedClaim: any | null = null;
  // Filters
  searchForm!: FormGroup;
  userBanks: any[] = [];
  userAgencies: any[] = [];
  isLoadingBanks = false;
  isLoadingAgencies = false;
  private _validatedBackup: any[] = [];
  public sortColumn: string = 'claimNumber';
  public sortDirection: 'asc' | 'desc' = 'asc';

  private readonly CLAIM_API_URL = `${environment.MSClaim}/api/claims`;
  private readonly VALIDATED_STATUSES = ['current', 'courant', 'validated', 'validé'];
  private readonly timeOutMax: number = 120000;

  constructor(
    private auth: AuthService,
    private toast: ToastService,
    private http: HttpClient,
    private bankAssuranceService: BankAssuranceService,
    private dialog: MatDialog
    , private fb: FormBuilder
  ) { }


  ngOnInit(): void {
    this.initSearchForm();
    this.loadClaimsToTransfer();
    this.loadFilterData();
    // TODO: charger l'historique réel
    this.transferHistory = [];
  }

  private loadFilterData(): void {
    this.isLoadingBanks = true;
    this.bankAssuranceService.authenticate(parseInt(localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0')).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) { this.isLoadingBanks = false; return; }
        const token = authResponse.token.includes(',') ? authResponse.token.split(',')[0].trim() : authResponse.token;

        // Use the same helper as ProducerSinistreList: get banks from APIM wrapper
        this.bankAssuranceService.getBanksFromApim(token).pipe(
          timeout(this.timeOutMax),
          catchError(() => of([])),
          finalize(() => this.isLoadingBanks = false)
        ).subscribe((resp: any[]) => {
          if (!Array.isArray(resp)) { this.userBanks = []; return; }
          // normalize to {id,name}
          this.userBanks = resp.map(b => ({ id: b.id ?? b.bankId ?? b.idBank, name: b.name || b.bankName || b.label || '' }));
        }, () => { this.userBanks = []; this.isLoadingBanks = false; });
      },
      error: () => { this.isLoadingBanks = false; }
    });
  }

  private loadAgenciesForBank(bankId: any): void {
    if (!bankId) { this.userAgencies = []; return; }
    this.isLoadingAgencies = true;
    this.bankAssuranceService.authenticate(parseInt(localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0')).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) { this.isLoadingAgencies = false; return; }
        const token = authResponse.token.includes(',') ? authResponse.token.split(',')[0].trim() : authResponse.token;

        // Use APIM wrapper to get branches/agencies for the bank
        this.bankAssuranceService.getBranchesByBankFromApim(token, bankId).pipe(
          timeout(this.timeOutMax),
          catchError(() => of([])),
          finalize(() => this.isLoadingAgencies = false)
        ).subscribe((resp: any[]) => {
          if (!Array.isArray(resp)) { this.userAgencies = []; return; }
          this.userAgencies = resp.map(a => ({ id: a.id ?? a.branchId ?? a.idBranch, name: a.name || a.branchName || a.label || '' }));
        }, () => { this.userAgencies = []; this.isLoadingAgencies = false; });
      }, error: () => { this.isLoadingAgencies = false; }
    });
  }

  private initSearchForm(): void {
    this.searchForm = this.fb.group({
      claimNumber: [''],
      incidenceDateMin: [''],
      incidenceDateMax: [''],
      notificationDateMin: [''],
      notificationDateMax: [''],
      bankId: [null],
      agencyId: [null]
    });
  }

  public onSearchSubmit(): void {
    this.currentPage = 1;
    this.queryClaims(this.searchForm.value || {});
  }

  public onBankSelected(bankId: any): void {
    this.loadAgenciesForBank(bankId);
  }

  private extractClaimsFromResponse(resp: any): any[] {
    if (Array.isArray(resp)) return resp;
    if (resp?.items && Array.isArray(resp.items)) return resp.items;
    if (resp && typeof resp === 'object') {
      for (const key of Object.keys(resp)) {
        if (Array.isArray(resp[key])) return resp[key];
      }
    }
    return [];
  }

  private mapClaimItem(c: any): any {
    return {
      id: c.id,
      claimNumber: c.claimNumber,
      productName: c.productName,
      incidenceDate: c.claimDetail?.incidenceDate || c.incidenceDate || '',
      notificationDate: c.claimDetail?.notificationDate || c.notificationDate || '',
      effectiveDate: c.claimDetail?.effectiveDate || c.effectiveDate || '',
      expirationDate: c.claimDetail?.expirationDate || c.expirationDate || '',
      contratManualNumber: c.contratManualNumber || c.contract?.manualNumber || c.contractNumber || '',
      policyID: c.policyIDHelios || '',
      riskElement: c.claimDetail?.rickElement || c.claimDetail?.riskElement || c.riskElement || c.riskElementDetail || '',
      status: c.status,
      full: c
    };
  }

  private queryClaims(paramsObj: any): void {
    this.loader = true;
    // Clear current lists so UI reflects loading/filter state immediately
    this.claimsToTransfer = [];
    this.transferHistory = [];
    this._validatedBackup = [];
    this.displayedClaims = [];
    this.displayedHistory = [];
    this.totalCount = 0;
    this.calculateTotalPages();
    this.updateDisplayed();
    this.bankAssuranceService.authenticate(parseInt(localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0')).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) { this.loader = false; return; }
        const token = authResponse.token.includes(',') ? authResponse.token.split(',')[0].trim() : authResponse.token;
        let params = new HttpParams();
        Object.keys(paramsObj || {}).forEach(k => { if (paramsObj[k] !== null && paramsObj[k] !== undefined && paramsObj[k] !== '') params = params.set(k, String(paramsObj[k])); });
        params = params.set('page', '1').set('pageSize', '10000');
        const headers = new HttpHeaders()
          .set('Authorization', `Bearer ${token}`)
          .set('Ocp-Apim-Subscription-Key', environment.Ocp_Apim_Subscription_Key || '');

        const url = `${this.CLAIM_API_URL}`;
        const toTransferParams = params.set('status', 'Current');
        const historyParams = params.set('status', 'SubmittedToHelios');

        forkJoin({
          toTransfer: this.http.get<any>(url, { headers, params: toTransferParams }).pipe(
            timeout(this.timeOutMax),
            catchError(() => of({ items: [] }))
          ),
          history: this.http.get<any>(url, { headers, params: historyParams }).pipe(
            timeout(this.timeOutMax),
            catchError(() => of({ items: [] }))
          )
        }).pipe(
          finalize(() => this.loader = false)
        ).subscribe((responses: any) => {
          const toTransferArr = this.extractClaimsFromResponse(responses.toTransfer);
          const historyArr = this.extractClaimsFromResponse(responses.history);

          this.claimsToTransfer = toTransferArr.map(c => this.mapClaimItem(c));
          this.transferHistory = historyArr.map(c => this.mapClaimItem(c));

          this._validatedBackup = [...this.claimsToTransfer];
          this.totalCount = this.claimsToTransfer.length;
          this.calculateTotalPages();
          this.updateDisplayed();
        }, () => {
          this.toast.showErrorToast('Impossible de charger les sinistres');
          this.claimsToTransfer = [];
          this.transferHistory = [];
          this._validatedBackup = [];
          this.displayedClaims = [];
          this.displayedHistory = [];
          this.totalCount = 0;
          this.calculateTotalPages();
          this.updateDisplayed();
        });
      }, error: () => { this.loader = false; this.toast.showErrorToast('Échec authentification'); }
    });
  }

  switchView(v: 'toTransfer' | 'history') {
    this.activeView = v;
    this.currentPage = 1;
    this.updateDisplayed();
  }

  private loadClaimsToTransfer(): void {
    this.currentPage = 1;
    this.queryClaims(this.searchForm?.value || {});
  }

  public transferClaim(claim: any): void {
    const full = claim.full || claim;
    this.toast.showInfoToast('Préparation du transfert pour ' + full.claimNumber);

    const warrantyCode = full.claimDetail?.warrantyCode || String(full.claimDetail?.subRick8ElementID || '');

    this.bankAssuranceService.authenticate(parseInt(localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID') || '0')).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) { this.toast.showErrorToast('Échec de l\'authentification'); return; }

        const headers = new HttpHeaders()
          .set('Authorization', `Bearer ${authResponse.token}`)
          .set('Ocp-Apim-Subscription-Key', environment.Ocp_Apim_Subscription_Key);

        const labelsUrl = `${environment.MSBank}/api/Warranty/Documents/ByWarrantyCode/${warrantyCode}`;

        // Get document labels then build transfer payload
        this.http.get<any[]>(labelsUrl, { headers }).pipe(finalize(() => { /** no-op */ })).subscribe({
          next: (labels: any[]) => {
            const labelsMap: { [id: number]: string } = {};
            if (Array.isArray(labels)) {
              labels.forEach(l => { if (l && l.id) labelsMap[l.id] = l.name; });
            }

            const comps = full.claimDetail?.complementClaim || [];
            const documents = comps.map((d: any) => ({
              id: d.id,
              labelId: d.label,
              labelName: labelsMap[d.label] || `Document ${d.label}`,
              filePath: d.filePath || null
            }));

            // Build Helios transfer payload (simulation only)
            const evalItems = (full.evaluationItems || []).map((e: any) => ({
              CoverageCode: e.coverageCode || e.CoverageCode || '',
              ItemType: e.itemType || e.ItemType || '',
              EvaluationAmount: e.evaluationAmount || e.EvaluationAmount || 0,
              ItemStatus: e.itemStatus || e.ItemStatus || '00001'
            }));

            const heliosPayload: any = {
              PolicyID: full.policyIDHelios || '',
              RiskElementID: 0,
              IncidentDate: full.claimDetail?.incidenceDate || full.incidenceDate || null,
              NotificationDate: full.claimDetail?.notificationDate || full.notificationDate || null,
              ClaimStatus: '00001',
              Circumstance: full.claimDetail?.circumstance || '',
              Consequence: full.claimDetail?.consequence || '',
              InitialEstimatingAmount: full.claimDetail?.initialEstimatingAmount || (evalItems.length ? evalItems[0].EvaluationAmount : 0),
              ClaimNumberManual: full.claimNumber || full.claimDetail?.claimNumberManual || full.contratManualNumber ||  '',
              EvaluationItems: evalItems
            };

            // Envoyer le payload vers Helios (endpoint submit-helios)
            const postUrl = `${this.CLAIM_API_URL}/${full.id}/submit-helios`;
            const postHeaders = new HttpHeaders()
              .set('Authorization', `Bearer ${authResponse.token}`)
              .set('Ocp-Apim-Subscription-Key', environment.Ocp_Apim_Subscription_Key || '')
              .set('Content-Type', 'application/json')
              .set('Accept', '*/*');


            // Use event reporting to show progress
            this.transfering = true;
            this.transferProgressById[String(full.id)] = 0;
            this.http.post(postUrl, heliosPayload, { headers: postHeaders, observe: 'events', reportProgress: true }).pipe(
              timeout(this.timeOutMax),
              catchError((err: any) => {
                console.error('Helios POST error:', err);
                this.toast.showErrorToast('Échec envoi vers Helios');
                // clear progress
                delete this.transferProgressById[String(full.id)];
                return of(null);
              }),
              finalize(() => { this.transfering = false; })
            ).subscribe((event: any) => {
              if (!event) return;
              if (event.type === HttpEventType.UploadProgress) {
                const percent = event.total ? Math.round(100 * event.loaded / event.total) : 0;
                this.transferProgressById[String(full.id)] = percent;
              } else if (event.type === HttpEventType.DownloadProgress) {
                const percent = event.total ? Math.round(100 * event.loaded / event.total) : (this.transferProgressById[String(full.id)] || 0);
                this.transferProgressById[String(full.id)] = percent;
              } else if (event.type === HttpEventType.Response) {
                const resp = event.body;
                console.log('Helios response:', resp);
                this.transferProgressById[String(full.id)] = 100;
                this.toast.showSuccessToast('Transfert effectué vers Helios');
                // Marquer le sinistre comme soumis à Helios et l'ajouter à l'historique
                const transferred = { ...full, status: 'SubmittedToHelios', transferredAt: new Date().toISOString(), heliosResponse: resp };
                // Retirer de la liste à transférer si présent
                this.claimsToTransfer = this.claimsToTransfer.filter(x => x.id !== full.id);
                this.transferHistory.unshift(transferred);
                // Mettre à jour l'affichage/pagination
                this.totalCount = this.claimsToTransfer.length;
                this.calculateTotalPages();
                this.updateDisplayed();
                // cleanup progress after short delay
                setTimeout(() => delete this.transferProgressById[String(full.id)], 2000);
              }
            });
          },
          error: () => {
            // Fallback: build payload without label names
            const comps = full.claimDetail?.complementClaim || [];
            const documents = comps.map((d: any) => ({ id: d.id, labelId: d.label, labelName: `Document ${d.label}`, filePath: d.filePath || null }));
            const payload = {
              claimId: full.id,
              claimNumber: full.claimNumber,
              contratManualNumber: full.contratManualNumber,
              productName: full.productName,
              policyID: full.policyID,
              status: full.status,
              documents: documents
            };
            console.log('Transfer payload (labels fetch failed):', payload);
            this.toast.showSuccessToast('Transfert simulé (échec récupération libellés)');
          }
        });
      },
      error: () => { this.toast.showErrorToast('Échec de l\'authentification'); }
    });
  }

  public onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortClaims(this.claimsToTransfer, column);
    this.sortClaims(this.transferHistory, column);
    this.updateDisplayed();
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  private updateDisplayed(): void {
    // Met à jour le total et la pagination selon la vue courante
    this.totalCount = this.activeView === 'toTransfer' ? this.claimsToTransfer.length : this.transferHistory.length;
    this.calculateTotalPages();
    const start = (this.currentPage - 1) * this.pageSize;
    if (this.activeView === 'toTransfer') {
      this.displayedClaims = this.claimsToTransfer.slice(start, start + this.pageSize);
    } else {
      this.displayedHistory = this.transferHistory.slice(start, start + this.pageSize);
    }
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updateDisplayed();
  }

  onPageSizeChange(event: any): void {
    this.pageSize = Number(event.target.value) || 10;
    this.currentPage = 1;
    this.calculateTotalPages();
    this.updateDisplayed();
  }

  getPaginationNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    if (this.totalPages <= 5) { for (let i = 1; i <= this.totalPages; i++) pages.push(i); }
    else {
      pages.push(1);
      if (this.currentPage > 3) pages.push('...');
      const start = Math.max(2, this.currentPage - 1);
      const end = Math.min(this.totalPages - 1, this.currentPage + 1);
      for (let i = start; i <= end; i++) if (!pages.includes(i)) pages.push(i);
      if (this.currentPage < this.totalPages - 2) pages.push('...');
      if (!pages.includes(this.totalPages)) pages.push(this.totalPages);
    }
    return pages;
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (['draft', 'brouillon'].includes(s)) return 'status-draft';
    if (['invalidated', 'invalidé'].includes(s)) return 'status-cancelled';
    if (['courant', 'validated', 'validé'].includes(s)) return 'status-validated';
    if (['proposal', 'proposal'].includes(s)) return 'status-proposal';
    return 'status-default';
  }

  getStatusLabel(status: string): string {
    const s = (status || '').toLowerCase();
    if (['courant', 'validated', 'validé'].includes(s)) return 'Current';
    if (['invalidated', 'invalidé'].includes(s)) return 'Invalidated';
    if (['draft', 'brouillon'].includes(s)) return 'Draft';
    return status || 'N/A';
  }

  private sortClaims(arr: any[], column: string): void {
    if (!arr || arr.length === 0) return;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    arr.sort((a: any, b: any) => {
      const getVal = (item: any) => {
        if (!item) return '';
        const v = item[column] !== undefined ? item[column] : (item.full && item.full[column] !== undefined ? item.full[column] : '');
        return v === null || v === undefined ? '' : v;
      };

      const va = getVal(a);
      const vb = getVal(b);

      // Try date compare
      const da = va ? Date.parse(String(va)) : NaN;
      const db = vb ? Date.parse(String(vb)) : NaN;
      if (!isNaN(da) && !isNaN(db)) {
        return (da - db) * dir;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
  }

  public openClaimDetails(claim: any): void {
    const payload = { ...(claim.full || claim), fromHistory: this.activeView === 'history' };
    const dialogRef = this.dialog.open(ModalTransferClaimComponent, {
      width: '720px',
      data: payload
    });

    dialogRef.afterClosed().subscribe((res: any) => {
      if (res?.action === 'transfer') this.transferClaim(res.claim || claim);
    });
  }

  retryTransfer(claim: any) {
    this.toast.showInfoToast('Relance transfert pour ' + claim.claimNumber);
    // TODO: relance du transfert
  }

  goBack() {
    history.back();
  }

  logOutPros() {
    this.auth.logout();
  }
}
